import { useMemo, useState } from "react";
import { characterList } from "@/mocks/characters";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import CharacterCard from "@/components/feature/CharacterCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const PAGE_SIZE = 8;

export default function CharactersPage() {
  useDocumentTitle(
    "角色介绍 - 漫蛙2动漫资讯平台",
    "漫蛙2角色介绍频道，收录热门动漫作品主要角色档案，支持首字母索引快速定位角色。"
  );
  const [letter, setLetter] = useState("全部");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const availableLetters = useMemo(() => new Set(characterList.map((c) => c.initial)), []);

  const filtered = useMemo(
    () => characterList.filter((c) => letter === "全部" || c.initial === letter),
    [letter]
  );

  const shown = filtered.slice(0, visible);

  return (
    <div className="pb-4">
      <section className="border-b border-background-200 bg-background-100">
        <div className="page-wrap py-8 md:py-12">
          <Breadcrumb items={[{ label: "角色介绍" }]} />
          <h1 className="mt-4 text-2xl font-extrabold text-foreground-950 md:text-3xl">角色介绍</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            收录热门动漫作品的主要角色档案。使用下方首字母索引快速定位，点击角色查看完整设定与关联资讯。
          </p>
        </div>
      </section>

      <div className="page-wrap mt-8">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground-700">
            <i className="ri-sort-alphabet-asc text-accent-600"></i>
            首字母索引
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setLetter("全部");
                setVisible(PAGE_SIZE);
              }}
              className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                letter === "全部" ? "bg-accent-500 text-background-50" : "bg-background-100 text-foreground-600 hover:text-accent-700"
              }`}
            >
              全部
            </button>
            {ALPHABET.map((item) => {
              const enabled = availableLetters.has(item);
              return (
                <button
                  key={item}
                  type="button"
                  disabled={!enabled}
                  onClick={() => {
                    setLetter(item);
                    setVisible(PAGE_SIZE);
                  }}
                  className={`flex h-8 w-8 items-center justify-center rounded-md text-sm font-medium transition-colors ${
                    letter === item
                      ? "bg-accent-500 text-background-50"
                      : enabled
                      ? "bg-background-100 text-foreground-600 hover:text-accent-700"
                      : "cursor-not-allowed bg-background-100/50 text-foreground-300"
                  }`}
                >
                  {item}
                </button>
              );
            })}
          </div>
        </div>

        {shown.length === 0 ? (
          <div className="mt-8">
            <EmptyState
              title="暂无该首字母的角色"
              description="试试选择其他字母，或查看全部角色档案。"
              actionLabel="查看全部角色"
              actionTo="/characters"
            />
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 md:gap-5">
            {shown.map((item) => (
              <CharacterCard key={item.id} item={item} />
            ))}
          </div>
        )}

        {visible < filtered.length && (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-700"
            >
              <i className="ri-add-line"></i>
              加载更多角色
            </button>
          </div>
        )}
      </div>
    </div>
  );
}